import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { extrairTokenEquipe } from '@/lib/portao';
import {
  lerFuncaoPendente,
  limparFuncaoPendente,
} from '@/lib/app';
import Modal from '@/components/ui/Modal';
import Botao from '@/components/ui/Botao';
import LeitorQR from './LeitorQR';

interface EntrarNaFuncaoProps {
  aberto: boolean;
  aoFechar: () => void;
}

// "Entrar numa função": lê o QR do cartaz com a câmera OU cola o link.
// Se este celular guardou um link de função (aberto antes de instalar),
// oferece abrir direto.
export default function EntrarNaFuncao({ aberto, aoFechar }: EntrarNaFuncaoProps) {
  const navigate = useNavigate();
  const [pendente, setPendente] = useState<string | null>(null);
  const [colado, setColado] = useState('');
  const [lendo, setLendo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!aberto) return;
    setPendente(lerFuncaoPendente());
    setColado('');
    setErro(null);
    setLendo(false);
  }, [aberto]);

  function irPara(token: string | null) {
    if (!token) {
      setErro('Não encontramos o link da função. Confira o QR ou o link e tente de novo.');
      return;
    }
    limparFuncaoPendente();
    aoFechar();
    navigate(`/equipe/${token}`);
  }

  function aoLerQR(texto: string) {
    const token = extrairTokenEquipe(texto);
    if (token) irPara(token);
    else setErro('Este QR não é de uma função do Se Liga.');
  }

  return (
    <Modal aberto={aberto} titulo="Entrar numa função" aoFechar={aoFechar}>
      {lendo ? (
        <div>
          <div className="h-[320px] w-full overflow-hidden rounded-2xl bg-black">
            <LeitorQR
              id="funcao-qr"
              ativo={aberto}
              aoLer={aoLerQR}
              aoErroCamera={() =>
                setErro('Não conseguimos abrir a câmera. Autorize o acesso ou cole o link abaixo.')
              }
            />
          </div>
          <p className="mt-3 text-center text-sm text-foreground-600">
            Aponte para o QR do cartaz da sua barraca.
          </p>
          <button
            type="button"
            onClick={() => setLendo(false)}
            className="mt-3 w-full cursor-pointer whitespace-nowrap text-center text-sm font-semibold text-primary-700"
          >
            Preferir colar o link
          </button>
        </div>
      ) : (
        <div>
          <p className="text-sm text-foreground-600">
            Aponte a câmera para o QR do cartaz ou cole o link da função.
          </p>

          {pendente && (
            <button
              type="button"
              onClick={() => irPara(pendente)}
              className="mt-4 flex w-full cursor-pointer items-center gap-3 rounded-2xl border border-primary-200 bg-primary-50 px-4 py-3 text-left transition-colors hover:bg-primary-100"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-500 text-white">
                <i className="ri-qr-code-line text-xl" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-bold text-foreground-950">
                  Abrir a função deste celular
                </span>
                <span className="block truncate text-xs text-foreground-500">
                  Você abriu este link antes de instalar o app.
                </span>
              </span>
            </button>
          )}

          <Botao
            tamanho="lg"
            cheio
            icone="ri-qr-scan-2-line"
            className="mt-4"
            onClick={() => {
              setErro(null);
              setLendo(true);
            }}
          >
            Ler o QR do cartaz
          </Botao>

          <div className="mt-4">
            <label htmlFor="funcao-link" className="block text-sm font-semibold text-foreground-800">
              Ou cole o link da função
            </label>
            <input
              id="funcao-link"
              type="url"
              value={colado}
              onChange={(e) => {
                setColado(e.target.value);
                setErro(null);
              }}
              placeholder="https://.../equipe/..."
              className="mt-1 w-full rounded-xl border border-secondary-300 bg-white px-4 py-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
            <Botao
              variante="secundario"
              className="mt-3"
              cheio
              icone="ri-login-circle-line"
              onClick={() => irPara(extrairTokenEquipe(colado))}
            >
              Abrir a função
            </Botao>
          </div>
        </div>
      )}

      {erro && (
        <p className="mt-3 flex items-start gap-1.5 text-sm text-danger-600" role="alert">
          <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
            <i className="ri-error-warning-line" />
          </span>
          {erro}
        </p>
      )}
    </Modal>
  );
}